/**
 * Copyright (c) 2021-2024 Kognitos, Inc. All rights reserved.
 */
import { CheckboxChangeEvent } from 'antd/es/checkbox';
import { useState } from 'react';

interface CheckboxSelectionState<T> {
  selected: T[];
  lastClickedId: T | null;
}

function useMultiSelectCheckboxes<T>(allItems: T[]): {
  selections: T[];
  handleCheckboxClick: (id: T, event: CheckboxChangeEvent) => void;
  resetSelections: () => void;
} {
  const [state, setState] = useState<CheckboxSelectionState<T>>({
    selected: [],
    lastClickedId: null
  });

  const handleCheckboxClick = (id: T, event: CheckboxChangeEvent) => {
    event.stopPropagation(); // Prevent event bubbling

    let newSelected = [...state.selected];
    const lastClickedIndex = allItems.indexOf(state.lastClickedId as T);
    const currentClickedIndex = allItems.indexOf(id);

    if (
      event.nativeEvent.shiftKey &&
      state.lastClickedId !== null &&
      lastClickedIndex !== -1 &&
      currentClickedIndex !== -1
    ) {
      // Shift + Click: Select range
      const rangeStart = Math.min(lastClickedIndex, currentClickedIndex);
      const rangeEnd = Math.max(lastClickedIndex, currentClickedIndex);
      const rangeItems = allItems.slice(rangeStart, rangeEnd + 1);

      const isRangeSelected = rangeItems.every((item) =>
        newSelected.includes(item)
      );
      if (isRangeSelected) {
        newSelected = newSelected.filter((item) => !rangeItems.includes(item));
      } else {
        newSelected = Array.from(new Set([...newSelected, ...rangeItems]));
      }
    } else {
      // Regular Click: Replace selection
      newSelected = state.selected.includes(id)
        ? state.selected.filter((s) => s !== id)
        : [...state.selected, id];
    }

    setState({ selected: newSelected, lastClickedId: id });
  };

  const resetSelections = () => {
    setState({ selected: [], lastClickedId: null });
  };

  return { selections: state.selected, handleCheckboxClick, resetSelections };
}

export default useMultiSelectCheckboxes;
